package com.dualremotetest

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

// The Battle City–style co-op game: rules, stage flow and menus. Fixed 60 Hz steps (the Famicom's frame), driven
// by any display rate through an accumulator, so timing never depends on the frame rate. Plain Kotlin; rendering
// (PixelRenderer), audio (TankAudio) and Android glue (TankGameActivity) only read this state or call in.
//
// Rule sources, in order of authority: the ROM notes in vgrichina/battlecity REVERSE.md (marked ROM), then the
// cattle-bity / shinima reimplementations (marked cross-reference), then our own choices (marked adaptation).
// docs/BATTLE_CITY_FIDELITY.md lists every value and where it came from.

enum class Phase { TITLE, SETTINGS, JOIN, STAGE_INTRO, PLAYING, PAUSED, TALLY, GAME_OVER, HIGH_SCORE, VICTORY, LOBBY }

enum class MoveMode(val label: String) { CONTINUOUS("CONTINUOUS"), GRID("GRID") }

/**
 * Every level plays each stage's own map and 20 enemies, in a random order (our change; the ROM spawns them in a fixed
 * order). The enemy mix per stage ([mix]): HARD the original; NORMAL with half of the armor tanks made basic; EASY with
 * no armor and half of the power tanks made basic. Spawn interval: NORMAL the ROM's 190 − 4 × stage; HARD stage 35's
 * (the ROM's second-loop rule, `Respawn_Delay_Calc`); EASY 1.5 × NORMAL's. EASY also has half as much enemy fire and
 * half as many HQ attacks. Lives are 3 on every level.
 * - [carriers]: the item-carrying enemies, as 1-based positions in the stage's spawn order (NORMAL: the ROM's 4th,
 *   11th and 18th; EASY and HARD are our balance choices).
 * - [itemWeights]: the odds of each item per drop, in half-percent units (sum 200). NORMAL and HARD use the ROM's
 *   table (star and grenade 2 in 8, the others 1 in 8); EASY's are ours.
 * - [itemLimit]: uncollected items on the field at once; a new drop beyond it removes the oldest. Only players take
 *   items: unlike the ROM, a newly spawned carrier no longer clears the uncollected item, which looked as if an enemy
 *   had taken it.
 */
enum class Difficulty(
    val label: String,
    val blurb: String,
    val carriers: Set<Int>,
    val itemWeights: Map<PowerUpType, Int>,
    val itemLimit: Int,
) {
    EASY("EASY", "FEWER AND CALMER ENEMIES", setOf(2, 5, 8, 11, 14, 17), weights(star = 50, grenade = 30, helmet = 30, clock = 30, shovel = 30, tank = 30), 2),
    NORMAL("NORMAL", "", setOf(4, 11, 18), ROM_ITEM_WEIGHTS, 1),
    HARD("HARD", "TOUGHER AND FASTER ENEMIES", setOf(7, 15), ROM_ITEM_WEIGHTS, 1);

    init {
        require(itemWeights.values.sum() == ITEM_ROLL) { "$name item weights must add up to $ITEM_ROLL" }
    }

    /**
     * The stage's 20 enemy types (in B, F, P, A order) from its original [roster]: HARD unchanged; NORMAL turns half of
     * the armor tanks (rounded down) into basic ones; EASY turns every armor tank and half of the power tanks (rounded
     * down) into basic ones.
     */
    fun mix(roster: String): String {
        var b = roster.count { it == 'B' }
        val f = roster.count { it == 'F' }
        var p = roster.count { it == 'P' }
        var a = roster.count { it == 'A' }
        when (this) {
            EASY -> { b += a + p / 2; p -= p / 2; a = 0 }
            NORMAL -> { b += a / 2; a -= a / 2 }
            HARD -> Unit
        }
        return "B".repeat(b) + "F".repeat(f) + "P".repeat(p) + "A".repeat(a)
    }

    /**
     * The item for a roll in 0 until [ITEM_ROLL]; each type covers as many rolls as its weight. With [swapStarAndTank]
     * the star and tank odds trade places.
     */
    fun itemFor(roll: Int, swapStarAndTank: Boolean = false): PowerUpType {
        var r = roll
        for (t in PowerUpType.entries) {
            val w = itemWeights[t] ?: 0
            if (r < w) return if (!swapStarAndTank) t else when (t) {
                PowerUpType.STAR -> PowerUpType.TANK
                PowerUpType.TANK -> PowerUpType.STAR
                else -> t
            }
            r -= w
        }
        error("roll $roll outside 0 until $ITEM_ROLL")
    }
}

/** Item rolls are in half-percent steps: 0 until 200. */
const val ITEM_ROLL = 200

private fun weights(star: Int, grenade: Int, helmet: Int, clock: Int, shovel: Int, tank: Int) = mapOf(
    PowerUpType.HELMET to helmet, PowerUpType.CLOCK to clock, PowerUpType.SHOVEL to shovel,
    PowerUpType.STAR to star, PowerUpType.GRENADE to grenade, PowerUpType.TANK to tank,
)

/** ROM PowerupTypeTable (helmet, clock, shovel, star, grenade, tank, grenade, star) as half-percent weights. */
private val ROM_ITEM_WEIGHTS = weights(star = 50, grenade = 50, helmet = 25, clock = 25, shovel = 25, tank = 25)

enum class Ending { NONE, CLEARED, DEFEATED }

enum class EngineSound { OFF, IDLE, MOVING }

enum class MenuItem { ONE_PLAYER, TWO_PLAYERS, ONLINE, SETTINGS, EXIT, RESUME, RESTART, DIFFICULTY, MOVEMENT, SMOOTHING, MUSIC, SFX, DIAGNOSTICS, SAVE_REPORT, QUIT, BACK }

/** Score tally after a stage, with the ROM's pacing (8 frames per kill, 20 between rows, 30 before the total). */
class Tally(val stage: Int, val outcome: Ending, val twoPlayer: Boolean, kills: List<IntArray>) {
    val kills: List<IntArray> = kills.map { it.copyOf() }
    val shown = List(2) { IntArray(4) }
    var row = 0
        internal set
    var totalShown = false
        internal set
    var bonusPlayer = -1
        internal set
    var finished = false
        internal set
    internal var wait = 32
    internal var rowHadKills = false
    /** Frames the finished tally has waited for the victory jingle to end. */
    var heldForJingle = 0
        internal set
    /** Frames since the results screen opened (FIRE skips it only after [TankMatch.TALLY_SKIP_GUARD_FRAMES]; online play times the victory jingle with it). */
    var frames = 0
        internal set

    fun total(p: Int) = kills[p].sum()
}

class TankMatch(val input: CoopInput, private val log: (String) -> Unit) {
    var clock: () -> Long = { nanoTime() / 1_000_000 }
    var audio: TankAudio = NoAudio
        set(value) {
            field = value
            currentMusic = Music.NONE // a new audio output starts silent
            value.setVolumes(musicVolume, sfxVolume)
        }
    var onSettingsChanged: () -> Unit = {}
    /** Saves the session report; returns the file path or null. Wired by the Activity. */
    var onSaveReport: () -> String? = { null }
    var onExit: () -> Unit = {}
    /** False in the browser, where a page cannot close itself: the title has no EXIT and BACK there does nothing. */
    var canExit = true
    /** Title → ONLINE GAME: opens the online lobby (set up by OnlinePlay). */
    var onOnline: () -> Unit = {}
    /** The online lobby's screens while [phase] is LOBBY. */
    var lobby: Lobby? = null
    /**
     * Online play: both phones run this match in lockstep, changing it only through [tick] and the key events the
     * session hands to both in the same order. Nothing local (focus, app pauses, audio timing) may change it then.
     */
    var lockstep = false
        private set
    /** This phone's player in an online game (0 or 1; -1 offline). Display only: it differs between the two phones. */
    var onlinePlayer = -1
        private set
    /** Online: the game time of the frame being run (frame × 16 ms), the same on both phones; set by the session. */
    var lockstepNowMs = 0L
    /** The input clock: this phone's own offline, the shared game time online (hold-to-fire must agree on both). */
    private fun inputNow() = if (lockstep) lockstepNowMs else clock()
    /** Shown over the picture during an online game, for example while waiting for the other phone. */
    var netStatus: String? = null
    private var offline: OfflineSettings? = null
    /** A phone or tablet (touchscreen, not a TV): the menus' hint lines name the touch controls and controllers. */
    var touchDevice = false
    /** A computer's browser (keyboard and mouse): the hint lines name the keys, and a click picks a menu row. */
    var keyboardHints = false
    /**
     * Browsers keep a page silent until its first click, tap or key press, and none lets a page start sound sooner. So
     * there a PRESS START screen comes before the title menu: pressing it lets the sound start, and the menu then shows
     * with its music playing, as on the TV.
     */
    var pressStart = false
        private set
    private var pressStartEndedAt = Long.MIN_VALUE / 2

    fun showPressStart() {
        pressStart = true
    }

    /** The press that ends the PRESS START screen (any key, click or tap) does nothing else, nor do keys for a moment. */
    fun dismissPressStart() {
        if (!pressStart) return
        pressStart = false
        titleScroll = 0
        pressStartEndedAt = clock()
        event("PRESS START pressed")
    }

    // ------------------------------------------------------------------ settings (persisted by the Activity)
    var moveMode = MoveMode.CONTINUOUS
        private set
    /** The Settings choice. A run keeps the difficulty it started with ([runDifficulty]). */
    var difficulty = Difficulty.NORMAL
        private set
    var runDifficulty = Difficulty.NORMAL
        private set
    var musicVolume = 75
        private set
    var sfxVolume = 75
        private set
    var showDiagnostics = false
        private set
    var hiScore = DEFAULT_HI_SCORE
        private set

    // ------------------------------------------------------------------ state read by the renderer
    var phase = Phase.TITLE
        private set
    var uiFrame = 0L
        private set
    var stageFrame = 0
        internal set
    val terrain = Terrain()
    val nav = Navigator(terrain)
    val players = listOf(PlayerTank(0), PlayerTank(1))
    var twoPlayer = false
        private set
    var stage = 1
        private set
    val enemies = ArrayList<EnemyTank>()
    val bullets = ArrayList<Bullet>()
    val explosions = ArrayList<Explosion>()
    val popups = ArrayList<ScorePopup>()
    /** Uncollected items on the field, oldest first (at most [Difficulty.itemLimit]). */
    val powerUps = ArrayList<PowerUp>()
    /** The newest uncollected item. */
    val powerUp: PowerUp? get() = powerUps.lastOrNull()
    var hqAlive = true
        internal set
    /** Enemies of this stage not yet spawned (the HUD shows one icon each, as the original does). */
    var toSpawn = ENEMIES_PER_STAGE
        internal set
    var killed = 0
        internal set
    /** Enemies of this stage still to destroy: on the field and still to come (the HUD icons count these). */
    val enemiesLeft get() = ENEMIES_PER_STAGE - killed
    var clockTicks = 0
        internal set
    /** Shovel in the ROM's 64-frame ticks (20 at pickup); steel until 3, then the wall flashes brick/steel. */
    var shovelTicks = 0
        internal set
    var ending = Ending.NONE
        private set
    var endTimer = 0
        private set
    var tally: Tally? = null
        private set
    var newRecord = false
        private set
    /** The Got High Score jingle has been started for this stage's clear (once per stage, never again for a record). */
    var stageJinglePlayed = false
        private set
    /** The Game Over cue has been started for this stage's defeat. */
    var gameOverCuePlayed = false
        private set
    var screenTimer = 0
        private set
    var introSelect = false
        private set
    var titleScroll = 0
        private set
    var menuCursor = 0
        private set
    var banner: String? = null
        private set
    private var bannerUntil = 0L
    var lastReportPath: String? = null
        private set
    var pauseReason = ""
        private set
    var defeatReason = ""
        private set

    internal val rng = JavaRandom()
    var seed = 0L
        private set
    /** Tests and simulations fix the run's random seed; normal play leaves it null. */
    internal var fixedSeed: Long? = null
    /** Simulation switches (never changed in normal play). */
    internal var hqInvulnerableForTest = false
    /** Spare tanks a new run starts with, instead of the original's, so long simulations are not cut short. */
    internal var startLivesForTest: Int? = null
    /** Enemy bullets that reached the eagle while [hqInvulnerableForTest] was set (simulations measure HQ pressure). */
    internal var hqHitsBlocked = 0
    internal var enemyFireEnabled = true
    val ai = EnemyAi(this)
    internal var lastStepBlocked = false
        private set
    private var spawnTimer = 0
    private var spawnRot = 0
    /** Game steps since the app started; only bullet-slot timers use it. */
    private var slotClock = 0
    private var nextEnemyId = 1
    private val pendingFort = HashMap<Int, Int>()
    private val knownX = IntArray(2) { -1 }
    private val knownY = IntArray(2) { -1 }
    private var engine = EngineSound.OFF
    private var snapshot: List<IntArray> = emptyList()

    // ------------------------------------------------------------------ observations (report, diagnostics panel)
    val frames = FrameStats()
    var stepsLastFrame = 0
        private set
    var catchUpDrops = 0
        private set
    private var lastFrameNanos = 0L
    private var accNanos = 0L
    val events = ArrayDeque<String>()
    val killsByType = IntArray(4)
    val pickupsByType = IntArray(PowerUpType.entries.size)
    val dropsByType = IntArray(PowerUpType.entries.size)
    var clashes = 0
        private set
    var shieldBlocks = 0
        private set
    var enemyShots = 0
        private set
    var maxConcurrent = 0
        private set
    var spawnWaitFrames = 0
        private set
    var stagesCleared = 0
        private set
    var runsStarted = 0
        private set
    var bothMovingSegments = 0
        private set
    var bothMovingLongest = 0
        private set
    var bothMovingTotal = 0
        private set
    private var bothMovingRun = 0
    var outcomeText = "none yet"
        private set

    /** The stage whose spawn interval applies: stage 35 on HARD, as in the original's second loop. */
    val spawnStage get() = if (runDifficulty == Difficulty.HARD) StageData.STAGES else stage
    /** This stage's 20 enemy types in spawn order: the difficulty's [Difficulty.mix], shuffled when the stage starts. */
    var roster = ""
        private set
    /**
     * The minimum gap between enemy arrivals (frames). ROM: 190 − 4 × stage − 20 with two players. EASY: 1.5 × that
     * with one player; with two, our own curve [easyTwoPlayerGap] (the gap after a kill is unchanged).
     */
    val spawnDelay: Int get() {
        val rom = 190 - 4 * spawnStage - if (twoPlayer) 20 else 0
        return when {
            runDifficulty != Difficulty.EASY -> rom
            twoPlayer -> easyTwoPlayerGap(spawnStage)
            else -> rom * 3 / 2
        }
    }
    /**
     * Enemies on the field at once, by the mode chosen (not by how many tanks are in play). Our balance; the ROM has 4
     * with one player and 6 with two.
     */
    val maxAlive get() = when (runDifficulty) {
        Difficulty.EASY -> if (twoPlayer) 2 else 1
        Difficulty.NORMAL -> if (twoPlayer) 4 else 2
        Difficulty.HARD -> if (twoPlayer) 6 else 3
    }
    val participants get() = if (twoPlayer) players else players.subList(0, 1)
    /** EASY (our balance): once every player has the most stars, a tank (extra life) is as likely as a star was. */
    val starsMaxed get() = runDifficulty == Difficulty.EASY && participants.all { it.stars >= MAX_STARS }
    fun isParticipant(p: PlayerTank) = p.index == 0 || twoPlayer

    // ================================================================== frame driver

    /** Call once per display frame (Choreographer / withFrameNanos time). Runs whole 60 Hz steps only. */
    fun frame(nowNanos: Long) {
        if (lastFrameNanos == 0L) lastFrameNanos = nowNanos
        var dt = nowNanos - lastFrameNanos
        lastFrameNanos = nowNanos
        if (dt > 0) frames.record(dt / 1e6, currentTimeMillis(), stageFrame)
        if (dt > MAX_FRAME_NS) {
            dt = MAX_FRAME_NS
            catchUpDrops++
        }
        accNanos += dt
        var steps = 0
        while (accNanos >= STEP_NS && steps < MAX_STEPS) {
            tick()
            accNanos -= STEP_NS
            steps++
        }
        if (steps == MAX_STEPS) accNanos = 0
        stepsLastFrame = steps
    }

    /** One 1/60 s step. Public for tests and simulations. */
    fun tick() {
        uiFrame++
        if (banner != null && uiFrame >= bannerUntil) banner = null
        when (phase) {
            Phase.TITLE -> if (titleScroll > 0 && !pressStart) titleScroll = max(0, titleScroll - 2)
            Phase.STAGE_INTRO -> {
                screenTimer++
                if (!introSelect && screenTimer >= INTRO_FRAMES) startStage()
            }
            Phase.PLAYING -> stepGame()
            Phase.TALLY -> stepTally()
            Phase.GAME_OVER, Phase.HIGH_SCORE, Phase.VICTORY -> {
                screenTimer++
                if (screenTimer >= screenLength()) advanceEndScreen()
            }
            Phase.PAUSED, Phase.JOIN, Phase.SETTINGS, Phase.LOBBY -> Unit
        }
    }

    // ================================================================== input

    /**
     * Every key except BACK comes here first (Activity.dispatchKeyEvent). Gameplay keys go to the owning player's
     * held state; on the other screens a fresh press navigates the pixel menus, from either remote. Returns true when
     * the key was used, so it never reaches Compose focus handling.
     */
    fun onKey(e: KeyInput): Boolean {
        val dir = Dir.ofKey(e.keyCode)
        val fire = e.keyCode in FIRE_KEYS
        if (dir == null && !fire) return false
        if (phase == Phase.PLAYING) {
            input.onGameKey(e)
            return true
        }
        if (!e.isDown || e.repeatCount != 0) {
            input.menuKeyUp(e)
            return true
        }
        if (pressStart) {
            dismissPressStart()
            return true
        }
        if (clock() - pressStartEndedAt < PRESS_START_GUARD_MS) return true
        when (phase) {
            Phase.TITLE -> if (titleScroll > 0) titleScroll = 0 else menuKey(dir, fire, e)
            Phase.SETTINGS, Phase.PAUSED -> menuKey(dir, fire, e)
            Phase.JOIN -> if (fire) {
                if (input.onAssignKey(e)) startRun(two = true)
                else if (input.assignMessage != null) flash("USE THE OTHER REMOTE")
            }
            Phase.STAGE_INTRO -> if (introSelect) {
                when {
                    fire -> startStage()
                    dir == Dir.UP || dir == Dir.RIGHT -> stage = stage % StageData.STAGES + 1
                    dir == Dir.DOWN || dir == Dir.LEFT -> stage = (stage + StageData.STAGES - 2) % StageData.STAGES + 1
                }
            }
            Phase.TALLY -> if (fire) skipTally()
            Phase.GAME_OVER, Phase.HIGH_SCORE, Phase.VICTORY -> if (fire && screenTimer > 60) advanceEndScreen()
            Phase.LOBBY -> lobby?.key(dir, fire)
            else -> Unit
        }
        return true
    }

    /** BACK (or Esc). Returns what it did, for the key log. */
    fun back(): String = when (phase) {
        Phase.PLAYING -> { pause("BACK"); "pause" }
        Phase.PAUSED -> { resume(); "resume" }
        Phase.SETTINGS -> { openTitle(scroll = false); "back to title" }
        Phase.JOIN -> { openTitle(); "back to title" }
        Phase.STAGE_INTRO -> if (introSelect) { openTitle(); "back to title" } else "ignored (stage intro)"
        Phase.TITLE -> if (canExit) { onExit(); "leave game" } else "ignored (title)"
        Phase.TALLY -> "ignored (score tally; FIRE skips it)"
        Phase.GAME_OVER, Phase.HIGH_SCORE, Phase.VICTORY -> { openTitle(); "to title" }
        Phase.LOBBY -> { lobby?.back(); "lobby back" }
    }

    fun menuItems(): List<MenuItem> = when (phase) {
        // Phones and tablets: one player per screen. 2 PLAYERS (both on one screen) is for the TV with two remotes.
        Phase.TITLE -> (if (touchDevice) PHONE_TITLE_ITEMS else TITLE_ITEMS).let { if (canExit) it else it - MenuItem.EXIT }
        Phase.SETTINGS -> SETTINGS_ITEMS
        Phase.PAUSED -> if (lockstep) ONLINE_PAUSE_ITEMS else PAUSE_ITEMS
        else -> emptyList()
    }

    fun itemLabel(item: MenuItem): String = when (item) {
        MenuItem.ONE_PLAYER -> "1 PLAYER"
        MenuItem.TWO_PLAYERS -> "2 PLAYERS"
        MenuItem.ONLINE -> "ONLINE GAME"
        MenuItem.SETTINGS -> "SETTINGS"
        MenuItem.EXIT -> "EXIT"
        MenuItem.RESUME -> "RESUME"
        MenuItem.RESTART -> "RESTART STAGE"
        MenuItem.DIFFICULTY -> "DIFFICULTY ${difficulty.label}"
        MenuItem.MOVEMENT -> "MOVEMENT  ${moveMode.label}"
        MenuItem.SMOOTHING -> "SMOOTHING ${if (input.smoothingMs == 0L) "OFF" else "${input.smoothingMs}MS"}"
        MenuItem.MUSIC -> "MUSIC     $musicVolume%"
        MenuItem.SFX -> "SOUND FX  $sfxVolume%"
        MenuItem.DIAGNOSTICS -> "DIAGNOSTICS ${if (showDiagnostics) "ON" else "OFF"}"
        MenuItem.SAVE_REPORT -> "SAVE REPORT"
        MenuItem.QUIT -> "QUIT TO TITLE"
        MenuItem.BACK -> "BACK"
    }

    /** The mouse points at menu row [i] (see PixelRenderer.menuRowAt): it becomes the selected row. */
    fun pointAtMenuRow(i: Int) {
        // Online the menu cursor is part of the shared game: only keys, sent to both devices, may move it.
        if (lockstep) return
        if (i in menuItems().indices) menuCursor = i
    }

    private fun menuKey(dir: Dir?, fire: Boolean, e: KeyInput) {
        val items = menuItems()
        if (items.isEmpty()) return
        menuCursor = menuCursor.coerceIn(0, items.size - 1)
        val item = items[menuCursor]
        when {
            dir == Dir.UP -> menuCursor = (menuCursor + items.size - 1) % items.size
            dir == Dir.DOWN -> menuCursor = (menuCursor + 1) % items.size
            dir == Dir.LEFT -> adjust(item, -1)
            dir == Dir.RIGHT -> adjust(item, 1)
            fire -> activate(item, e)
        }
    }

    private fun adjust(item: MenuItem, delta: Int) {
        when (item) {
            MenuItem.DIFFICULTY -> {
                val d = Difficulty.entries
                setDifficulty(d[(difficulty.ordinal + delta + d.size) % d.size])
            }
            MenuItem.MOVEMENT -> setMoveMode(if (moveMode == MoveMode.CONTINUOUS) MoveMode.GRID else MoveMode.CONTINUOUS)
            MenuItem.SMOOTHING -> {
                val c = CoopInput.SMOOTHING_CHOICES
                val i = (c.indexOf(input.smoothingMs).coerceAtLeast(0) + delta + c.size) % c.size
                input.smoothingMs = c[i]
                event("Smoothing → ${if (c[i] == 0L) "off" else "${c[i]} ms"} (experimental; directions only, never fire or menus)")
            }
            MenuItem.MUSIC -> setVolumes(stepVolume(musicVolume, delta), sfxVolume)
            MenuItem.SFX -> {
                setVolumes(musicVolume, stepVolume(sfxVolume, delta))
                audio.sfx(Sfx.POWERUP_PICKUP)
            }
            MenuItem.DIAGNOSTICS -> showDiagnostics = !showDiagnostics
            else -> return
        }
        onSettingsChanged()
    }

    private fun activate(item: MenuItem, e: KeyInput) {
        when (item) {
            MenuItem.ONE_PLAYER, MenuItem.TWO_PLAYERS -> {
                // The remote that confirms becomes Player 1; a connected but idle keyboard never joins by itself.
                input.resetAssignments()
                input.onAssignKey(e)
                if (item == MenuItem.ONE_PLAYER) startRun(two = false) else phase = Phase.JOIN
            }
            MenuItem.ONLINE -> onOnline()
            MenuItem.SETTINGS -> { phase = Phase.SETTINGS; menuCursor = 0 }
            MenuItem.EXIT -> onExit()
            MenuItem.RESUME -> resume()
            MenuItem.RESTART -> restartStage()
            MenuItem.SAVE_REPORT -> saveReport()
            MenuItem.QUIT -> openTitle()
            MenuItem.BACK -> openTitle(scroll = false)
            else -> adjust(item, 1)
        }
    }

    fun applySettings(
        mode: MoveMode, smoothingMs: Long, music: Int, sfx: Int, diagnostics: Boolean, hi: Int,
        difficulty: Difficulty = Difficulty.NORMAL,
    ) {
        this.difficulty = difficulty
        moveMode = mode
        input.smoothingMs = if (smoothingMs in CoopInput.SMOOTHING_CHOICES) smoothingMs else 0L
        musicVolume = music.coerceIn(0, 100)
        sfxVolume = sfx.coerceIn(0, 100)
        showDiagnostics = diagnostics
        hiScore = max(hi, 0)
        audio.setVolumes(musicVolume, sfxVolume)
    }

    /** Takes effect when the next run starts. */
    fun setDifficulty(d: Difficulty) {
        difficulty = d
        event("Difficulty → ${d.label} (from the next run)")
    }

    fun setMoveMode(mode: MoveMode) {
        moveMode = mode
        players.forEach { it.gridTarget = null }
        input.players.forEach { it.stepRequests.clear() }
        event("Movement → ${mode.label}")
    }

    fun setVolumes(music: Int, sfx: Int) {
        musicVolume = music.coerceIn(0, 100)
        sfxVolume = sfx.coerceIn(0, 100)
        audio.setVolumes(musicVolume, sfxVolume)
    }

    private fun stepVolume(v: Int, delta: Int): Int {
        val s = AudioLevels.VOLUME_STEPS
        val i = s.indexOfFirst { it >= v }.let { if (it < 0) s.lastIndex else it }
        return s[(i + delta).coerceIn(0, s.lastIndex)]
    }

    fun toggleDiagnostics() {
        showDiagnostics = !showDiagnostics
        onSettingsChanged()
    }

    fun saveReport() {
        val path = onSaveReport()
        lastReportPath = path
        flash(if (path != null) "REPORT SAVED" else "REPORT NOT SAVED")
        event("Report ${if (path != null) "saved to $path" else "could not be saved"} (the match continues unchanged)")
    }

    private fun flash(text: String, frames: Int = 150) {
        banner = text
        bannerUntil = uiFrame + frames
    }

    // ================================================================== lifecycle hooks

    fun pause(reason: String) {
        if (phase != Phase.PLAYING) return
        // Online only BACK pauses, handed to both phones by the session: one phone pausing alone would split the game.
        if (lockstep && reason != "BACK") return
        phase = Phase.PAUSED
        pauseReason = reason
        menuCursor = 0
        input.clearAllHeld()
        endBothMoving()
        audio.setGamePaused(true)
        audio.sfx(Sfx.PAUSE)
        event("Paused ($reason)")
    }

    fun resume() {
        if (phase != Phase.PAUSED) return
        input.clearAllHeld()
        lastFrameNanos = 0
        accNanos = 0
        phase = Phase.PLAYING
        audio.setGamePaused(false)
        event("Resumed")
    }

    fun onFocusLost() {
        if (lockstep) return // the online session releases this phone's held keys through the game's own input stream
        input.clearAllHeld()
        pause("window lost focus")
    }

    fun onDevicesChanged(devices: List<DeviceInfo>) {
        val lost = input.syncDevices(devices)
        if (lost.isEmpty()) return
        val who = lost.joinToString(" AND ") { "P${it.number}" }
        event("$who remote disconnected")
        if (phase == Phase.PLAYING) pause("remote disconnected")
        flash("$who REMOTE DISCONNECTED", 400)
    }

    // ================================================================== flow

    /** [scroll]: the title slides in (as in the original) except when coming back from Settings. */
    fun openTitle(scroll: Boolean = true) {
        phase = Phase.TITLE
        titleScroll = if (scroll) TITLE_SCROLL else 0
        menuCursor = 0
        tally = null
        input.clearAllHeld()
        setEngine(EngineSound.OFF)
        audio.setGamePaused(false)
        // Calm loop on the title and menus (our addition); it keeps playing through settings, joining and stage select.
        if (currentMusic != Music.MENU) playMusic(Music.MENU)
    }

    /** The cue last started (cues never overlap: each one replaces the previous). */
    var currentMusic = Music.NONE
        private set

    private fun playMusic(m: Music) {
        currentMusic = m
        audio.music(m)
    }

    /**
     * Online play: both phones start the same 2-player run from the same state (the host's settings, a shared seed,
     * the better of the two high scores). [local] is this phone's player (0 = host, 1 = guest). This phone's own
     * settings come back with [endOnlineRun].
     */
    fun startOnlineRun(cfg: OnlineConfig, local: Int) {
        if (!lockstep) offline = OfflineSettings(difficulty, moveMode, input.smoothingMs)
        lockstep = true
        onlinePlayer = local
        difficulty = cfg.difficulty
        moveMode = cfg.moveMode
        input.smoothingMs = 0 // smoothing reads this phone's clock
        hiScore = cfg.hiScore
        fixedSeed = cfg.seed
        input.bindOnline(local)
        menuCursor = 0
        banner = null
        netStatus = null
        event("ONLINE RUN as P${local + 1} · ${cfg.encode()}")
        startRun(two = true)
    }

    fun openLobby(l: Lobby) {
        lobby = l
        phase = Phase.LOBBY
        menuCursor = 0
    }

    fun closeLobby() {
        lobby = null
        openTitle(scroll = false)
    }

    /** Leaves an online game for the title, with this phone's own settings back and [message] shown. */
    fun endOnlineRun(message: String?) {
        if (!lockstep) return
        lockstep = false
        onlinePlayer = -1
        fixedSeed = null
        offline?.let {
            difficulty = it.difficulty
            moveMode = it.moveMode
            input.smoothingMs = it.smoothingMs
            // A setting changed online (MOVEMENT) saved the shared values on this phone: store its own again.
            onSettingsChanged()
        }
        offline = null
        input.resetAssignments()
        netStatus = null
        event("ONLINE RUN END${message?.let { " · $it" } ?: ""}")
        openTitle(scroll = false)
        message?.let { flash(it, 300) }
    }

    /** A checksum of what the two phones of an online game must agree on; the session compares them every second. */
    fun stateHash(): Long {
        var h = 1125899906842597L
        fun mix(v: Int) { h = 31 * h + v }
        mix(phase.ordinal); mix(stage); mix(stageFrame); mix(killed); mix(toSpawn); mix(if (hqAlive) 1 else 0)
        mix(ending.ordinal); mix(menuCursor); mix(screenTimer); mix(clockTicks); mix(shovelTicks)
        for (p in players) {
            mix(p.state.ordinal); mix(p.x); mix(p.y); mix(p.dir.ordinal); mix(p.lives); mix(p.stars); mix(p.score)
            mix(p.timer); mix(p.helmetTicks); mix(p.reviveFrames)
        }
        mix(enemies.size)
        for (e in enemies) { mix(e.type.ordinal); mix(e.x); mix(e.y); mix(e.dir.ordinal); mix(e.hp); mix(e.state.ordinal); mix(e.timer) }
        mix(bullets.size)
        for (b in bullets) { mix(b.x); mix(b.y); mix(b.dir.ordinal); mix(if (b.alive) 1 else 0) }
        for (pu in powerUps) { mix(pu.type.ordinal); mix(pu.x); mix(pu.y) }
        mix(terrain.bricksBroken); mix(terrain.steelBroken)
        return h
    }

    /** Starts a run at the chosen stage, after the stage-select screen. Player 1 (and 2) are already assigned. */
    fun startRun(two: Boolean) {
        twoPlayer = two
        runDifficulty = difficulty
        seed = fixedSeed ?: nanoTime()
        rng.setSeed(seed)
        runsStarted++
        newRecord = false
        stage = 1 // as the original's new-game setup does; the stage screen then lets the players pick
        players.forEach { p ->
            p.state = if (isParticipant(p)) PlayerState.DEAD else PlayerState.ABSENT
            p.lives = if (isParticipant(p)) startLivesForTest ?: START_LIVES else 0
            p.stars = 0
            p.score = 0
            p.deaths = 0; p.respawns = 0; p.shots = 0; p.shotsRefused = 0; p.pickups = 0; p.maxStars = 0
            p.reviveFrames = 0; p.revivals = 0
        }
        ai.reset()
        event("RUN ${if (two) "2 PLAYERS" else "1 PLAYER"} · difficulty ${runDifficulty.label} · seed $seed · " +
            input.players.filter { it.isAssigned }.joinToString(" · ") { "P${it.number} ${it.channelText()}" })
        openStageIntro(select = true)
    }

    private fun openStageIntro(select: Boolean) {
        phase = Phase.STAGE_INTRO
        introSelect = select
        screenTimer = 0
        setEngine(EngineSound.OFF)
        input.clearAllHeld()
    }

    private fun startStage() {
        // The difficulty's mix of this stage's enemy types, in a random order (seeded with the run).
        val origRoster = StageData.ROSTERS[stage - 1]
        val types = runDifficulty.mix(origRoster).toCharArray()
        for (k in types.size - 1 downTo 1) {
            val o = rng.nextInt(k + 1)
            val t = types[k]; types[k] = types[o]; types[o] = t
        }
        roster = types.concatToString()
        terrain.load(stage)
        nav.refresh()
        enemies.clear(); bullets.clear(); explosions.clear(); popups.clear()
        powerUps.clear()
        hqAlive = true
        toSpawn = ENEMIES_PER_STAGE
        killed = 0
        spawnTimer = 0
        spawnRot = 0
        clockTicks = 0
        shovelTicks = 0
        pendingFort.clear()
        ending = Ending.NONE
        endTimer = 0
        stageJinglePlayed = false
        gameOverCuePlayed = false
        stageFrame = 0
        knownX.fill(-1); knownY.fill(-1)
        bothMovingRun = 0
        // A pending revival carries over: its countdown continues (in game time) once this stage is running.
        snapshot = players.map { intArrayOf(it.lives, it.stars, it.score, if (it.state == PlayerState.OUT) 1 else 0, it.reviveFrames) }
        players.forEach { p ->
            p.helmetTicks = 0
            p.spawnShield = 0
            p.slotFreeAt.fill(0)
            p.stageKills.fill(0)
            p.gridTarget = null
            p.slideLeft = 0
            p.revealed = 0
            p.moving = false
            if (isParticipant(p) && p.state != PlayerState.OUT && p.lives > 0) beginSpawn(p)
        }
        input.clearAllHeld()
        input.players.forEach { it.stepRequests.clear() }
        lastFrameNanos = 0
        accNanos = 0
        phase = Phase.PLAYING
        playMusic(Music.STAGE_START)
        event("STAGE $stage start · ${runDifficulty.label} · enemies in order $roster (original $origRoster) · spawn every $spawnDelay frames · up to $maxAlive enemies · " +
            "lives ${participants.joinToString("/") { "P${it.index + 1} ${it.lives}" }}")
    }

    private fun restartStage() {
        players.forEachIndexed { i, p ->
            val s = snapshot.getOrNull(i) ?: return@forEachIndexed
            p.lives = s[0]; p.stars = s[1]; p.score = s[2]
            p.state = if (s[3] == 1) PlayerState.OUT else if (isParticipant(p)) PlayerState.DEAD else PlayerState.ABSENT
            p.reviveFrames = s[4]
        }
        event("Restart stage $stage (lives, stars, score and any pending revival as at the start of the stage)")
        audio.setGamePaused(false)
        startStage()
    }

    private fun finishStage() {
        val outcome = ending
        val t = Tally(stage, outcome, twoPlayer, players.map { it.stageKills })
        tally = t
        if (outcome == Ending.CLEARED) stagesCleared++
        phase = Phase.TALLY
        setEngine(EngineSound.OFF)
        // Our victory jingle is the Got High Score cue (the original melody if you supply that file). It plays once on
        // the results screen of every cleared stage (all 20 enemies destroyed, HQ standing), the last one included.
        // The flag keeps it to one start per stage.
        if (outcome == Ending.CLEARED && hqAlive && !stageJinglePlayed) {
            stageJinglePlayed = true
            playMusic(Music.HIGH_SCORE)
            event("Victory jingle (Got High Score cue) started on the results screen")
        }
        event("STAGE $stage ${if (outcome == Ending.CLEARED) "CLEARED" else "LOST ($defeatReason)"} after %.1f s · kills P1 %s%s"
            .format(stageFrame / 60.0, players[0].stageKills.joinToString("/"), if (twoPlayer) " P2 ${players[1].stageKills.joinToString("/")}" else ""))
    }

    /**
     * FIRE on the results screen: the counts and any 2-player bonus are settled at once and the next stage (or the end
     * of the run) follows without waiting for the count or the victory jingle, which stops.
     */
    private fun skipTally() {
        val t = tally ?: return
        if (t.frames < TALLY_SKIP_GUARD_FRAMES) return // a shot fired just as the field closed is not a skip
        for (p in 0..1) t.kills[p].copyInto(t.shown[p])
        t.row = 4
        if (!t.totalShown) showTallyTotal(t)
        event("Results screen skipped with FIRE after %.1f s".format(t.frames / 60.0))
        if (currentMusic == Music.HIGH_SCORE) playMusic(Music.NONE) // the victory jingle stops with the screen
        leaveTally(t)
    }

    private fun leaveTally(t: Tally) {
        if (t.heldForJingle > 0) event("Results screen held %.1f s for the victory jingle to end".format(t.heldForJingle / 60.0))
        when {
            t.outcome == Ending.DEFEATED -> endRun(victory = false)
            stage >= StageData.STAGES -> endRun(victory = true)
            else -> {
                stage++
                openStageIntro(select = false)
            }
        }
    }

    private fun showTallyTotal(t: Tally) {
        t.totalShown = true
        if (t.twoPlayer && t.outcome == Ending.CLEARED && t.total(0) != t.total(1)) {
            // ROM: in 2-player games the player with more kills on the stage gets a 1000-point bonus.
            t.bonusPlayer = if (t.total(0) > t.total(1)) 0 else 1
            players[t.bonusPlayer].score += 1000
        }
        t.wait = 135
        t.finished = true
    }

    private fun stepTally() {
        val t = tally ?: return
        t.frames++
        if (t.finished) {
            if (--t.wait > 0) return
            // The jingle ends before anything else plays (the next Stage Start, the menu music). A file that never
            // reports its end is cut off after JINGLE_HOLD_MAX_FRAMES. Online, both phones must move on at the same
            // frame, so the bundled jingle's length counts there instead of this phone's player.
            val jingleOn = stageJinglePlayed && currentMusic == Music.HIGH_SCORE &&
                (if (lockstep) t.frames < ONLINE_JINGLE_FRAMES else audio.isPlaying(Music.HIGH_SCORE))
            if (jingleOn && t.heldForJingle < JINGLE_HOLD_MAX_FRAMES) {
                t.heldForJingle++
                return
            }
            leaveTally(t)
            return
        }
        if (--t.wait > 0) return
        if (t.row < 4) {
            var advanced = false
            for (p in 0..1) if (t.shown[p][t.row] < t.kills[p][t.row]) {
                t.shown[p][t.row]++
                advanced = true
            }
            if (advanced) {
                audio.sfx(Sfx.TALLY_TICK)
                t.rowHadKills = true
                t.wait = 9
            } else {
                t.row++
                t.wait = if (t.row == 4) 47 else if (t.rowHadKills) 30 else 30
                t.rowHadKills = false
            }
            return
        }
        if (!t.totalShown) showTallyTotal(t)
    }

    private fun endRun(victory: Boolean) {
        val best = participants.maxOf { it.score }
        newRecord = best > hiScore
        if (newRecord) {
            hiScore = best
            onSettingsChanged()
        }
        outcomeText = (if (victory) "campaign complete (stage 35 cleared)" else "game over at stage $stage ($defeatReason)") +
            " · scores ${participants.joinToString(" / ") { "P${it.index + 1} ${it.score}" }}${if (newRecord) " · NEW HIGH SCORE" else ""}"
        event("RUN END: $outcomeText")
        phase = if (victory) Phase.VICTORY else Phase.GAME_OVER
        screenTimer = 0
        // No cue of its own: Game Over already played on the field and the stage-35 jingle on the results screen.
    }

    private fun screenLength() = when (phase) {
        Phase.GAME_OVER -> 300
        Phase.HIGH_SCORE -> 420
        else -> 480
    }

    private fun advanceEndScreen() {
        if ((phase == Phase.GAME_OVER || phase == Phase.VICTORY) && newRecord) {
            phase = Phase.HIGH_SCORE
            screenTimer = 0
            // ROM: the record screen plays Got High Score. After stage 35 it has just played as the stage jingle, so
            // the record set at the same moment does not start it a second time.
            if (!stageJinglePlayed) playMusic(Music.HIGH_SCORE)
        } else {
            openTitle()
        }
    }

    // ================================================================== one game step

    private fun stepGame() {
        stageFrame++
        slotClock++
        if (stageFrame % 64 == 0) {
            // ROM: helmet, clock and shovel count down in 64-frame ticks.
            players.forEach { if (it.helmetTicks > 0) it.helmetTicks-- }
            if (clockTicks > 0 && --clockTicks == 0) event("Clock ended – enemies move again")
            if (shovelTicks > 0 && --shovelTicks == 0) {
                setFort(T.BRICK)
                event("Shovel ended – HQ wall back to brick")
            }
        }
        // ROM: for the last 3 ticks (192 frames) the wall alternates brick and steel every 16 frames, brick first.
        if (shovelTicks in 1..3 && stageFrame % 16 == 0) setFort(if ((stageFrame / 16) % 2 == 0) T.BRICK else T.STEEL)
        applyPendingFort()
        input.expireGrace(inputNow())
        if (moveMode == MoveMode.CONTINUOUS) input.players.forEach { it.stepRequests.clear() }
        nav.refresh()
        updateKnowledge()
        for (p in players) updatePlayer(p)
        updateEnemies()
        updateSpawner()
        updateBullets()
        updatePowerUp()
        explosions.forEach { it.age++ }
        explosions.removeAll { it.age >= it.length }
        popups.forEach { if (it.delay > 0) it.delay-- else it.age++ }
        popups.removeAll { it.age >= it.length }
        trackBothMoving()
        checkEnd()
        updateEngineSound()
    }

    private fun checkEnd() {
        // ROM: lives and the HQ are checked again after the tally, so losing the last life (or the HQ, see destroyHq)
        // after the 20th kill still ends the run. We then show the usual defeat sequence.
        // With two players, one may be out and waiting to revive; once both are out of tanks, the run is over at once.
        if (ending != Ending.DEFEATED && participants.all { it.state == PlayerState.OUT }) {
            ending = Ending.DEFEATED
            endTimer = GAME_OVER_FRAMES
            defeatReason = "all players out of lives" + if (killed >= ENEMIES_PER_STAGE) " after the last enemy" else ""
            event("All participating players are out of lives")
            cancelRevivals("no teammate left")
            playMusic(Music.NONE)
            return
        }
        if (ending == Ending.NONE) {
            if (killed >= ENEMIES_PER_STAGE) {
                ending = Ending.CLEARED
                endTimer = CLEAR_WAIT_FRAMES
                event("All $ENEMIES_PER_STAGE enemies of stage $stage destroyed")
            }
            return
        }
        // Game Over plays on the field, from the moment the GAME OVER text starts to rise (our placement).
        if (ending == Ending.DEFEATED && endTimer <= GAME_OVER_FRAMES && !gameOverCuePlayed) {
            gameOverCuePlayed = true
            playMusic(Music.GAME_OVER)
            event("Game Over cue started on the field")
        }
        if (--endTimer <= 0) finishStage()
    }

    // ------------------------------------------------------------------ players

    private fun beginSpawn(p: PlayerTank) {
        val x = Spots.PLAYER_X[p.index]
        if (solidAt(x, Spots.PLAYER_Y, p)) {
            p.state = PlayerState.DEAD
            p.timer = 1 // adaptation: wait until the spawn point is clear instead of spawning on top of a tank
            return
        }
        p.x = x
        p.y = Spots.PLAYER_Y
        p.dir = Dir.UP
        p.moveAcc = 0
        p.state = PlayerState.SPAWNING
        p.timer = PLAYER_SPAWN_STAR_FRAMES
        if (terrain.clearBlock(x, Spots.PLAYER_Y)) event("P${p.index + 1} spawn block cleared (ROM rule)")
    }

    private fun updatePlayer(p: PlayerTank) {
        val pin = input.players[p.index]
        when (p.state) {
            PlayerState.ABSENT -> return
            PlayerState.OUT -> {
                pin.fireRequested = false
                if (p.reviveFrames > 0 && ending != Ending.DEFEATED && --p.reviveFrames == 0) revive(p)
                return
            }
            PlayerState.DEAD -> {
                pin.fireRequested = false
                if (--p.timer <= 0) beginSpawn(p)
                return
            }
            PlayerState.SPAWNING -> {
                pin.fireRequested = false
                if (--p.timer <= 0) {
                    p.state = PlayerState.ACTIVE
                    p.helmetTicks = SPAWN_HELMET_TICKS // ROM: every spawn gets a 3-tick helmet (129-192 frames)
                    if (stageFrame > PLAYER_SPAWN_STAR_FRAMES + 1) p.respawns++
                }
                return
            }
            PlayerState.ACTIVE -> Unit
        }
        if (p.spawnShield > 0) p.spawnShield--
        if (p.revealed > 0) p.revealed--
        if (ending == Ending.DEFEATED) {
            p.moving = false
            pin.fireRequested = false
            return
        }
        movePlayer(p, pin)
        if (pin.fireRequested) {
            pin.fireRequested = false
            tryFire(p)
        } else {
            // Holding fire keeps firing, whenever a bullet slot is free; a short press fires once.
            val since = pin.fireHeldSince()
            if (since != null && inputNow() - since >= HOLD_FIRE_MS && slotsInUse(p) < p.bulletLimit()) tryFire(p)
        }
    }

    private fun movePlayer(p: PlayerTank, pin: CoopPlayerInput) {
        val onIce = terrain.at(p.cx, p.cy) == T.ICE
        if (moveMode == MoveMode.CONTINUOUS) {
            val want = pin.effectiveDirection(inputNow())?.let(Dir::ofKey)
            if (want != null) {
                turn(p, want)
                advance(p, PLAYER_SPEED_Q)
                p.slideLeft = if (onIce) ICE_SLIDE_PX else 0
                p.slideDir = p.dir
                p.moving = true
                return
            }
        } else {
            if (p.gridTarget == null) {
                pin.stepRequests.removeFirstOrNull()?.let(Dir::ofKey)?.let { req ->
                    turn(p, req)
                    p.gridDir = p.dir
                    p.gridTarget = gridTargetFor(p)
                    p.slideLeft = 0
                }
            }
            val target = p.gridTarget
            if (target != null) {
                val c = if (p.gridDir.isHorizontal) p.x else p.y
                advance(p, PLAYER_SPEED_Q, abs(target - c))
                val now = if (p.gridDir.isHorizontal) p.x else p.y
                p.moving = true
                if (now == target || lastStepBlocked) {
                    p.gridTarget = null
                    p.slideLeft = if (onIce && !lastStepBlocked) ICE_SLIDE_PX else 0
                    p.slideDir = p.dir
                }
                return
            }
        }
        // No input: on ice the tank slides a short fixed distance (stopping at obstacles); elsewhere it stops at once.
        if (p.slideLeft > 0) {
            p.dir = p.slideDir
            val moved = advance(p, PLAYER_SPEED_Q, p.slideLeft)
            p.slideLeft = if (lastStepBlocked) 0 else p.slideLeft - moved
            p.moving = true
            return
        }
        p.moving = false
        p.moveAcc = 0
    }

    private fun gridTargetFor(p: PlayerTank): Int {
        val c = if (p.dir.isHorizontal) p.x else p.y
        val t = if (p.dir == Dir.RIGHT || p.dir == Dir.DOWN) (c / 8 + 1) * 8 else if (c <= 0) 0 else (c - 1) / 8 * 8
        return t.coerceIn(0, FIELD - TANK)
    }

    /**
     * ROM: a fresh press fires if a bullet slot is free; there is no cooldown timer, but a slot stays busy for 9 frames
     * after its bullet hits something (freed at once when bullets collide or a shield absorbs it).
     */
    private fun tryFire(p: PlayerTank) {
        if (slotsInUse(p) >= p.bulletLimit()) {
            p.shotsRefused++
            return
        }
        // ROM: 1 star = power-1 bullets (4 px/frame), 2 stars = two bullets in flight, 3 stars = steel-breaking bullets.
        spawnBullet(p, fromPlayer = true, speed = if (p.stars >= 1) 4 else 2, pierce = p.stars >= 3)
        p.shots++
        p.revealed = REVEAL_FRAMES
        audio.sfx(Sfx.PLAYER_FIRE)
    }

    private fun updateKnowledge() {
        for (p in players) {
            if (!isParticipant(p) || !p.inPlay) continue
            if (!hiddenFromEnemies(p)) {
                knownX[p.index] = p.x
                knownY[p.index] = p.y
            }
        }
    }

    /** A player at least half inside forest is hidden, unless it fired in the last [REVEAL_FRAMES] frames. */
    fun hiddenFromEnemies(p: PlayerTank) = p.revealed == 0 && terrain.forestArea(p.x, p.y, TANK, TANK) * 2 >= TANK * TANK

    /** Where enemies last saw this player (null if never seen this stage). */
    fun knownNode(i: Int): Int? = if (knownX[i] < 0) null else nav.nearestNode(knownX[i], knownY[i])

    // ------------------------------------------------------------------ enemies

    private fun updateEnemies() {
        val frozen = clockTicks > 0
        for (e in enemies.toTypedArray()) {
            if (e.hitFlash > 0) e.hitFlash--
            if (e.state == EnemyState.SPAWNING) {
                if (--e.timer <= 0) e.state = EnemyState.ACTIVE
                continue
            }
            if (frozen) continue
            ai.update(e)
            if (canEnemyFire(e) && rng.nextInt(fireOneIn(e)) == 0) enemyFire(e)
        }
    }

    /** ROM: about 1/32 per frame. Adaptation: likelier while facing the eagle or a player it can see; half as likely on EASY. */
    internal fun fireOneIn(e: EnemyTank): Int {
        val oneIn = when {
            e.brain.holding -> 12
            players.any { isParticipant(it) && it.inPlay && !hiddenFromEnemies(it) && fireLineClear(terrain, e.x, e.y, e.dir, it.x, it.y) } -> 12
            hqAlive && fireLineClear(terrain, e.x, e.y, e.dir, Spots.HQ_X, Spots.HQ_Y) -> 20
            else -> ENEMY_FIRE_ONE_IN
        }
        return if (runDifficulty == Difficulty.EASY) oneIn * 2 else oneIn
    }

    fun canEnemyFire(e: EnemyTank) = enemyFireEnabled && e.state == EnemyState.ACTIVE && clockTicks == 0 && ending == Ending.NONE &&
        slotsInUse(e) == 0

    fun enemyFire(e: EnemyTank) {
        spawnBullet(e, fromPlayer = false, speed = if (e.type.fastBullet) 4 else 2, pierce = false)
        enemyShots++
        audio.sfx(Sfx.ENEMY_FIRE)
    }

    private fun updateSpawner() {
        if (ending != Ending.NONE || toSpawn == 0) return
        if (spawnTimer > 0) spawnTimer--
        if (spawnTimer > 0) return
        // ROM: spawning and exploding enemies hold their slot too.
        if (enemies.size + explosions.count { it.kind == Boom.TANK || it.kind == Boom.FAST_TANK } >= maxAlive) {
            spawnWaitFrames++
            return
        }
        for (k in 0 until 3) {
            val idx = (spawnRot + k) % 3
            val x = Spots.ENEMY_X[idx]
            if (solidAt(x, Spots.ENEMY_Y, null)) continue
            spawnRot = (idx + 1) % 3
            val ordinal = ENEMIES_PER_STAGE - toSpawn + 1
            val type = EnemyType.ofCode(roster[ordinal - 1])
            val e = EnemyTank(nextEnemyId++, type, ordinal, ordinal in runDifficulty.carriers)
            e.x = x
            e.y = Spots.ENEMY_Y
            e.dir = Dir.DOWN
            e.timer = ENEMY_SPAWN_STAR_FRAMES
            enemies += e
            if (terrain.clearBlock(x, Spots.ENEMY_Y)) event("enemy spawn block at x=$x cleared (ROM rule)")
            toSpawn--
            spawnTimer = spawnDelay
            maxConcurrent = max(maxConcurrent, enemies.size)
            return
        }
        spawnWaitFrames++ // every spawn point is occupied; try again next frame
    }

    private fun hitEnemy(e: EnemyTank, by: PlayerTank) {
        if (e.carrier) {
            // ROM: the flashing tank drops its item on the first hit (armor tanks also lose that armor point).
            e.carrier = false
            dropPowerUp()
        }
        if (e.hp > 1) {
            e.hp--
            e.hitFlash = 10
            audio.sfx(Sfx.ARMOR_HIT)
            return
        }
        destroyEnemy(e, by)
    }

    private fun destroyEnemy(e: EnemyTank, by: PlayerTank?) {
        enemies.remove(e)
        if (e.ordinal > 0) killed++ // only the stage's own 20 count (test probes add extra tanks with ordinal 0)
        killsByType[e.type.ordinal]++
        val boom = if (e.type == EnemyType.FAST && by != null) Boom.FAST_TANK else Boom.TANK
        explosions += Explosion(e.cx, e.cy, boom)
        if (by != null) {
            by.score += e.type.points
            by.stageKills[e.type.ordinal]++
            popups += ScorePopup(e.cx, e.cy, e.type.points, boom.length, if (boom == Boom.FAST_TANK) 6 else 12)
        }
        audio.sfx(Sfx.ENEMY_EXPLOSION)
        event("enemy #${e.id} ${e.type.label} (${e.ordinal}/20) destroyed ${if (by != null) "by P${by.index + 1}" else "by grenade"} · $killed/20")
    }

    // ------------------------------------------------------------------ movement & collision

    /** Moves [t] along its direction at [speedQ] quarter-pixels per frame; stops at obstacles. Returns pixels moved. */
    fun advance(t: Tank, speedQ: Int, maxPx: Int = Int.MAX_VALUE): Int {
        lastStepBlocked = false
        t.moveAcc += speedQ
        var moved = 0
        while (t.moveAcc >= 4 && moved < maxPx) {
            if (!canStep(t, t.dir)) {
                lastStepBlocked = true
                t.moveAcc = 0
                break
            }
            t.x += t.dir.dx
            t.y += t.dir.dy
            t.tread++
            t.moveAcc -= 4
            moved++
        }
        if (moved >= maxPx) t.moveAcc = 0
        return moved
    }

    /**
     * One pixel in [d] is allowed if the newly covered 1-px leading strip is free of brick, steel and water, the HQ
     * and other tanks. Only the leading strip is tested, so a tank that already overlaps something (a wall rebuilt
     * around it) can always drive out. Tanks block each other but never damage each other (adaptation: the ROM notes
     * report no tank-tank collision at all; blocking avoids tanks stacking on top of each other).
     */
    fun canStep(t: Tank, d: Dir): Boolean {
        val nx = t.x + d.dx
        val ny = t.y + d.dy
        if (nx < 0 || ny < 0 || nx > FIELD - TANK || ny > FIELD - TANK) return false
        val s = strip(nx, ny, d)
        if (terrain.tankBlocked(s[0], s[1], s[2], s[3])) return false
        if (overlap(s[0], s[1], s[2], s[3], Spots.HQ_X, Spots.HQ_Y, TANK, TANK)) return false
        for (o in players) if (o !== t && o.solid && t.overlaps(nx, ny, o) && !t.overlaps(t.x, t.y, o)) return false
        for (o in enemies) if (o !== t && t.overlaps(nx, ny, o) && !t.overlaps(t.x, t.y, o)) return false
        return true
    }

    private fun strip(nx: Int, ny: Int, d: Dir): IntArray = when (d) {
        Dir.UP -> intArrayOf(nx, ny, TANK, 1)
        Dir.DOWN -> intArrayOf(nx, ny + TANK - 1, TANK, 1)
        Dir.LEFT -> intArrayOf(nx, ny, 1, TANK)
        Dir.RIGHT -> intArrayOf(nx + TANK - 1, ny, 1, TANK)
    }

    fun frontStrip(t: Tank): IntArray = strip(t.x + t.dir.dx, t.y + t.dir.dy, t.dir)

    fun tankAhead(t: Tank): Boolean {
        val nx = t.x + t.dir.dx
        val ny = t.y + t.dir.dy
        return players.any { it !== t && it.solid && t.overlaps(nx, ny, it) && !t.overlaps(t.x, t.y, it) } ||
            enemies.any { it !== t && t.overlaps(nx, ny, it) && !t.overlaps(t.x, t.y, it) }
    }

    /** Turns, snapping to the 8-px grid on a quarter turn as the original does (so 16-px gaps can be entered). */
    fun turn(t: Tank, d: Dir) {
        if (d == t.dir) return
        if (d.isHorizontal != t.dir.isHorizontal) snapForTurn(t, d)
        t.dir = d
    }

    private fun snapForTurn(t: Tank, d: Dir) {
        val snapX = !d.isHorizontal
        val c = if (snapX) t.x else t.y
        if (c % 8 == 0) return
        val lo = c - c % 8
        val hi = lo + 8
        val order = if (c - lo < hi - c) intArrayOf(lo, hi) else intArrayOf(hi, lo)
        for (v in order) {
            val nx = if (snapX) v else t.x
            val ny = if (snapX) t.y else v
            if (nx < 0 || ny < 0 || nx > FIELD - TANK || ny > FIELD - TANK) continue
            if (placeFree(t, nx, ny)) {
                t.x = nx
                t.y = ny
                return
            }
        }
    }

    private fun placeFree(t: Tank, nx: Int, ny: Int): Boolean {
        if (terrain.tankBlocked(nx, ny, TANK, TANK)) return false
        if (overlap(nx, ny, TANK, TANK, Spots.HQ_X, Spots.HQ_Y, TANK, TANK)) return false
        for (o in players) if (o !== t && o.solid && t.overlaps(nx, ny, o) && !t.overlaps(t.x, t.y, o)) return false
        for (o in enemies) if (o !== t && t.overlaps(nx, ny, o) && !t.overlaps(t.x, t.y, o)) return false
        return true
    }

    fun canTurnAndStep(t: Tank, d: Dir): Boolean {
        val x = t.x
        val y = t.y
        val dir = t.dir
        turn(t, d)
        val ok = canStep(t, d)
        t.x = x; t.y = y; t.dir = dir
        return ok
    }

    private fun solidAt(x: Int, y: Int, except: Tank?): Boolean =
        players.any { it !== except && it.solid && overlap(x, y, TANK, TANK, it.x, it.y, TANK, TANK) } ||
            enemies.any { it !== except && overlap(x, y, TANK, TANK, it.x, it.y, TANK, TANK) }

    // ------------------------------------------------------------------ bullets

    fun bulletsOf(t: Tank) = bullets.count { it.alive && it.owner === t }

    /** Bullets in flight plus slots still recovering from an impact. */
    fun slotsInUse(t: Tank) = bulletsOf(t) + t.slotFreeAt.count { it > slotClock }

    // Slot timers run on their own clock, which never restarts: the stage frame counter goes back to 0 on every
    // stage, restart and new run, and timers kept from before made both slots look busy afterwards (field report).
    private fun holdSlot(t: Tank) {
        val i = if (t.slotFreeAt[0] <= slotClock) 0 else 1
        t.slotFreeAt[i] = slotClock + SLOT_RECOVERY_FRAMES
    }

    private fun spawnBullet(t: Tank, fromPlayer: Boolean, speed: Int, pierce: Boolean) {
        // ROM FireBullet: the bullet starts 8 px from the tank's centre, at its front edge.
        val b = Bullet(t, fromPlayer, t.cx + t.dir.dx * 8, t.cy + t.dir.dy * 8, t.dir, speed, pierce)
        bullets += b
        checkBullet(b)
    }

    /**
     * Bullets advance in 1-px micro-steps (2 or 4 per frame) and are checked after every one, so nothing is skipped:
     * opposing bullets are compared after each micro-step, which catches pairs that cross between frames.
     */
    private fun updateBullets() {
        for (micro in 0 until 4) {
            for (b in bullets) {
                if (!b.alive || (b.speed < 4 && micro % 2 == 0)) continue
                b.x += b.dir.dx
                b.y += b.dir.dy
                checkBullet(b)
            }
            for (a in bullets) {
                if (!a.alive || !a.fromPlayer) continue
                for (o in bullets) {
                    if (!o.alive || o.fromPlayer) continue
                    if (abs(a.x - o.x) < 6 && abs(a.y - o.y) < 6) {
                        // ROM: a player bullet and an enemy bullet within 6 px destroy each other.
                        a.alive = false
                        o.alive = false
                        clashes++
                        break
                    }
                }
            }
        }
        bullets.removeAll { !it.alive }
    }

    private fun checkBullet(b: Bullet) {
        if (!b.alive) return
        if (b.x < 0 || b.y < 0 || b.x >= FIELD || b.y >= FIELD) {
            stopBullet(b, if (b.fromPlayer) Sfx.STEEL_HIT else null)
            return
        }
        // ROM 4-probe terrain check, perpendicular offsets 0 (A), +4 (B), -1 (C), -5 (D). A or C stops the bullet
        // on brick or steel; B and D only add brick damage when A / C broke brick.
        val v = !b.dir.isHorizontal
        fun px(o: Int) = if (v) b.x + o else b.x
        fun py(o: Int) = if (v) b.y else b.y + o
        val ra = probe(px(0), py(0), b)
        if (ra == HIT_BRICK) probe(px(4), py(4), b)
        val rc = probe(px(-1), py(-1), b)
        if (rc == HIT_BRICK) probe(px(-5), py(-5), b)
        if (ra != 0 || rc != 0) {
            val steel = ra == HIT_STEEL || rc == HIT_STEEL
            stopBullet(b, if (!b.fromPlayer) null else if (steel) Sfx.STEEL_HIT else Sfx.BRICK_HIT)
            return
        }
        if (inHq(px(0), py(0)) || inHq(px(-1), py(-1))) {
            if (hqAlive && !b.fromPlayer && hqInvulnerableForTest) hqHitsBlocked++
            if (hqAlive && !b.fromPlayer && !hqInvulnerableForTest) destroyHq() else stopBullet(b, if (b.fromPlayer) Sfx.STEEL_HIT else null)
            b.alive = false
            return
        }
        if (b.fromPlayer) {
            val hit = enemies.firstOrNull { it.state == EnemyState.ACTIVE && near(b, it) } ?: return
            b.alive = false
            holdSlot(b.owner)
            hitEnemy(hit, b.owner as PlayerTank)
        } else {
            // Player bullets pass through teammates (co-op adaptation); enemy bullets ignore other enemies.
            val hit = players.firstOrNull { isParticipant(it) && it.state == PlayerState.ACTIVE && near(b, it) } ?: return
            b.alive = false
            hitPlayer(hit, b)
        }
    }

    private fun near(b: Bullet, t: Tank) = abs(b.x - t.cx) < 10 && abs(b.y - t.cy) < 10

    private fun inHq(x: Int, y: Int) = x >= Spots.HQ_X && x < Spots.HQ_X + 16 && y >= Spots.HQ_Y && y < Spots.HQ_Y + 16

    private fun probe(x: Int, y: Int, b: Bullet): Int {
        if (x < 0 || y < 0 || x >= FIELD || y >= FIELD) return 0
        return when (terrain.at(x, y)) {
            T.BRICK -> if (b.pierceSteel) { terrain.breakTile(x shr 2, y shr 2); HIT_STEEL_BROKEN } else { terrain.breakBrick(x shr 2, y shr 2); HIT_BRICK }
            T.STEEL -> if (b.pierceSteel) { terrain.breakTile(x shr 2, y shr 2); HIT_STEEL_BROKEN } else HIT_STEEL
            else -> 0
        }
    }

    private fun stopBullet(b: Bullet, sound: Sfx?) {
        b.alive = false
        holdSlot(b.owner)
        explosions += Explosion(b.x, b.y, Boom.BULLET)
        sound?.let(audio::sfx)
    }

    private fun hitPlayer(p: PlayerTank, b: Bullet) {
        if (p.shielded) {
            shieldBlocks++ // ROM: absorbed without an explosion, and the enemy's slot frees at once
            return
        }
        holdSlot(b.owner)
        p.state = PlayerState.DEAD
        p.lives--
        // ROM: every death loses the stars. EASY (our balance): they survive death, a revival included.
        val keepStars = runDifficulty == Difficulty.EASY
        if (!keepStars) p.stars = 0
        p.deaths++
        p.slideLeft = 0
        p.gridTarget = null
        p.moving = false
        explosions += Explosion(p.cx, p.cy, Boom.PLAYER)
        audio.sfx(Sfx.PLAYER_EXPLOSION)
        if (p.lives > 0) {
            p.timer = RESPAWN_DELAY_FRAMES
            event("P${p.index + 1} destroyed · ${p.lives} tank(s) left · stars ${if (keepStars) "kept (EASY)" else "reset"}")
        } else {
            p.state = PlayerState.OUT
            // Co-op revival (our addition, every difficulty): back in 10 s with one tank, as long as the teammate still
            // has a tank (on the field, spawning or waiting for an ordinary respawn). Otherwise the run is over.
            val teammateIn = twoPlayer && participants.any { it !== p && it.state != PlayerState.OUT && it.lives > 0 }
            if (teammateIn && ending != Ending.DEFEATED) {
                p.reviveFrames = REVIVE_FRAMES
                event("P${p.index + 1} destroyed · out of lives · back in ${REVIVE_FRAMES / 60} s with one tank (teammate still in)")
            } else {
                event("P${p.index + 1} destroyed · out of lives")
            }
        }
    }

    /** The end of a revival countdown: one tank (no spares), the usual spawn point, star and 3-tick helmet. */
    private fun revive(p: PlayerTank) {
        p.lives = 1
        p.revivals++
        beginSpawn(p)
        event("P${p.index + 1} revived with one tank (revival ${p.revivals})")
    }

    private fun cancelRevivals(why: String) {
        players.filter { it.reviveFrames > 0 }.forEach {
            it.reviveFrames = 0
            event("P${it.index + 1} revival cancelled ($why)")
        }
    }

    private fun destroyHq() {
        hqAlive = false
        explosions += Explosion(Spots.HQ_X + 8, Spots.HQ_Y + 8, Boom.HQ)
        audio.sfx(Sfx.HQ_EXPLOSION)
        // Also after the 20th kill (an enemy bullet still in flight): the ROM checks the HQ again after the tally and
        // ends the run, so that stage is not cleared.
        if (ending != Ending.DEFEATED) {
            playMusic(Music.NONE) // quiet during the explosion; Game Over follows as the text rises
            defeatReason = if (ending == Ending.CLEARED) "HQ destroyed after the last enemy" else "HQ destroyed"
            ending = Ending.DEFEATED
            // ROM: GAME OVER starts rising after the eagle's 39-frame explosion.
            endTimer = GAME_OVER_FRAMES + Boom.HQ.length
            cancelRevivals("HQ destroyed")
        }
        event("HQ destroyed by an enemy bullet – run over")
    }

    // ------------------------------------------------------------------ power-ups and the HQ wall

    /**
     * ROM: the item appears on one of 16 fixed points (x and y each from 24/72/120/168), re-rolled if it would land on a
     * living player. Adaptation: points on steel or water, or cut off from the players, are skipped so items are
     * always reachable; if none qualifies, the nearest reachable grid point is used. A point covering an item that
     * stays on the field is skipped too (EASY keeps two).
     */
    private fun dropPowerUp() {
        if (powerUps.size >= runDifficulty.itemLimit) {
            val old = powerUps.removeAt(0)
            if (runDifficulty.itemLimit > 1) event("oldest uncollected item ${old.type.label} removed for the new drop")
        }
        val reach = nav.fromPlayerSpawns()
        fun free(x: Int, y: Int) = powerUps.none { abs(it.x - x) < 16 && abs(it.y - y) < 16 }
        fun ok(x: Int, y: Int) = reach[(y / 8) * NODES + x / 8] < Navigator.INF && free(x, y) &&
            players.none { isParticipant(it) && it.inPlay && abs(it.cx - (x + 8)) < 12 && abs(it.cy - (y + 8)) < 12 }
        val points = ITEM_POINTS.flatMap { y -> ITEM_POINTS.map { x -> x to y } }.filter { (x, y) -> ok(x, y) }
        val (x, y) = if (points.isNotEmpty()) points[rng.nextInt(points.size)] else {
            val all = (0 until NODES * NODES).filter { reach[it] < Navigator.INF }.map { (it % NODES) * 8 to (it / NODES) * 8 }
            if (all.isEmpty()) return
            all.filter { (ax, ay) -> free(ax, ay) }.ifEmpty { all }.minBy { (ax, ay) -> abs(ax - 96) + abs(ay - 96) }
        }
        val swap = starsMaxed
        val type = runDifficulty.itemFor(rng.nextInt(ITEM_ROLL), swapStarAndTank = swap)
        powerUps += PowerUp(type, x, y)
        dropsByType[type.ordinal]++
        audio.sfx(Sfx.POWERUP_APPEAR)
        event("power-up ${type.label} dropped at ($x,$y)${if (swap) " (every player at $MAX_STARS stars: star and tank odds swapped)" else ""}")
    }

    private fun updatePowerUp() {
        for (pu in powerUps.toList()) {
            pu.age++
            val p = players.firstOrNull {
                isParticipant(it) && it.inPlay && abs(it.cx - (pu.x + 8)) < 12 && abs(it.cy - (pu.y + 8)) < 12
            } ?: continue
            collect(p, pu)
        }
    }

    private fun collect(p: PlayerTank, pu: PowerUp) {
        powerUps.remove(pu)
        p.score += POWERUP_POINTS
        p.pickups++
        pickupsByType[pu.type.ordinal]++
        popups += ScorePopup(pu.x + 8, pu.y + 8, POWERUP_POINTS, 0, 49)
        when (pu.type) {
            PowerUpType.HELMET -> p.helmetTicks = HELMET_TICKS
            PowerUpType.CLOCK -> clockTicks = CLOCK_TICKS
            PowerUpType.SHOVEL -> if (hqAlive) {
                shovelTicks = SHOVEL_TICKS
                setFort(T.STEEL)
            }
            PowerUpType.STAR -> {
                p.stars = min(MAX_STARS, p.stars + 1)
                p.maxStars = max(p.maxStars, p.stars)
            }
            PowerUpType.GRENADE -> enemies.filter { it.state == EnemyState.ACTIVE }.forEach { destroyEnemy(it, null) }
            PowerUpType.TANK -> p.lives++
        }
        audio.sfx(if (pu.type == PowerUpType.TANK) Sfx.EXTRA_LIFE else Sfx.POWERUP_PICKUP)
        event("P${p.index + 1} collected ${pu.type.label}${if (pu.type == PowerUpType.STAR) " → ${p.stars} star(s)" else ""}" +
            if (pu.type == PowerUpType.TANK) " → ${p.lives} tank(s)" else "")
    }

    /** Sets every HQ wall cell; cells a tank is standing on are filled once it has moved away. */
    private fun setFort(type: Int) {
        pendingFort.clear()
        for (i in Terrain.FORT_CELLS) {
            if (tankOnCell(i)) pendingFort[i] = type else terrain.set(i % CELLS, i / CELLS, type)
        }
    }

    private fun applyPendingFort() {
        if (pendingFort.isEmpty()) return
        val it = pendingFort.entries.iterator()
        while (it.hasNext()) {
            val (i, type) = it.next()
            if (!tankOnCell(i)) {
                terrain.set(i % CELLS, i / CELLS, type)
                it.remove()
            }
        }
    }

    private fun tankOnCell(i: Int): Boolean {
        val x = (i % CELLS) * CELL
        val y = (i / CELLS) * CELL
        return players.any { it.solid && overlap(x, y, CELL, CELL, it.x, it.y, TANK, TANK) } ||
            enemies.any { overlap(x, y, CELL, CELL, it.x, it.y, TANK, TANK) }
    }

    /** Frames of each timed effect still to run (64-frame ticks are converted at the current phase of the tick). */
    fun helmetFramesLeft(p: PlayerTank) = ticksToFrames(p.helmetTicks)
    fun clockFramesLeft() = ticksToFrames(clockTicks)
    private fun ticksToFrames(t: Int) = if (t <= 0) 0 else (t - 1) * 64 + (64 - stageFrame % 64)
    fun shovelFramesLeft() = ticksToFrames(shovelTicks)

    // ------------------------------------------------------------------ audio state, observations

    private fun updateEngineSound() {
        val want = when {
            ending != Ending.NONE || stageFrame < STAGE_START_CUE_FRAMES -> EngineSound.OFF
            // A Stage Start file longer than the built-in cue: the engine waits until it has ended (at most 15 s).
            stageFrame < STAGE_START_MAX_FRAMES && currentMusic == Music.STAGE_START && audio.isPlaying(Music.STAGE_START) -> EngineSound.OFF
            participants.any { it.inPlay && it.moving } -> EngineSound.MOVING
            participants.any { it.solid } -> EngineSound.IDLE
            else -> EngineSound.OFF
        }
        setEngine(want)
    }

    private fun setEngine(s: EngineSound) {
        if (s == engine) return
        engine = s
        audio.engine(s)
    }

    private fun trackBothMoving() {
        val both = twoPlayer && players.all { it.inPlay && it.moving }
        if (both) bothMovingRun++ else endBothMoving()
    }

    private fun endBothMoving() {
        if (bothMovingRun >= 30) {
            bothMovingSegments++
            bothMovingTotal += bothMovingRun
            bothMovingLongest = max(bothMovingLongest, bothMovingRun)
        }
        bothMovingRun = 0
    }

    fun event(text: String) {
        val line = "[S%02d %6.1fs] %s".format(stage, stageFrame / 60.0, text)
        events.addLast(line)
        if (events.size > MAX_EVENTS) events.removeFirst()
        log("TANK $line")
    }

    private fun overlap(ax: Int, ay: Int, aw: Int, ah: Int, bx: Int, by: Int, bw: Int, bh: Int) =
        ax < bx + bw && bx < ax + aw && ay < by + bh && by < ay + ah

    // ================================================================== test and simulation hooks

    internal fun killForTest(e: EnemyTank, by: PlayerTank?) = destroyEnemy(e, by)

    /** Puts an active enemy on the field (tests and probes). It is not one of the stage's 20 and does not count toward them. */
    internal fun addEnemyForTest(type: EnemyType, x: Int, y: Int, dir: Dir, carrier: Boolean = false): EnemyTank {
        val e = EnemyTank(nextEnemyId++, type, 0, carrier)
        e.x = x
        e.y = y
        e.dir = dir
        e.state = EnemyState.ACTIVE
        enemies += e
        return e
    }

    /** Makes this the only item on the field. */
    internal fun placePowerUpForTest(type: PowerUpType, x: Int, y: Int) {
        powerUps.clear()
        powerUps += PowerUp(type, x, y)
    }

    /** A direct player hit (drops a carrier's item; armor loses a point). */
    internal fun hitForTest(e: EnemyTank, by: PlayerTank) = hitEnemy(e, by)

    internal fun fireForTest(t: Tank, fromPlayer: Boolean, speed: Int = 2, pierce: Boolean = false) = spawnBullet(t, fromPlayer, speed, pierce)

    // ================================================================== report

    fun reportSections(): List<Pair<String, String>> {
        val state = buildString {
            appendLine("phase=$phase stage=$stage/${StageData.STAGES} mode=${if (twoPlayer) "2 players" else "1 player"} stageTime=%.1f s ending=$ending"
                .format(stageFrame / 60.0))
            appendLine("enemies: destroyed $killed/20 · still to spawn $toSpawn · on field ${enemies.size} (max $maxAlive) · spawn delay $spawnDelay frames")
            appendLine("HQ ${if (hqAlive) "standing" else "destroyed"} · clock ${clockFramesLeft()} frames · shovel ${shovelFramesLeft()} frames · " +
                "items on field ${powerUps.joinToString { "${it.type.label} at (${it.x},${it.y})" }.ifEmpty { "none" }}")
            players.forEach { p ->
                appendLine("P${p.index + 1}: ${p.state} lives=${p.lives} stars=${p.stars} (max ${p.maxStars}) score=${p.score} pos=(${p.x},${p.y}) ${p.dir} " +
                    "shield=${p.spawnShield}f helmet=${helmetFramesLeft(p)}f deaths=${p.deaths} respawns=${p.respawns} revivals=${p.revivals} revive in ${p.reviveFrames}f shots=${p.shots} " +
                    "refused=${p.shotsRefused} pickups=${p.pickups} stageKills=${p.stageKills.joinToString("/")}")
            }
            enemies.forEach { e ->
                appendLine("  enemy #${e.id} ${e.type.label} ${e.state} pos=(${e.x},${e.y}) ${e.dir} hp=${e.hp} carrier=${e.carrier} " +
                    "goal=${e.brain.goal}${if (e.brain.goal == GoalKind.HQ) "/${e.brain.lane.label}" else ""} holding=${e.brain.holding}")
            }
            appendLine("outcome of the last run: $outcomeText · runs started $runsStarted · stages cleared $stagesCleared · seed $seed")
        }
        val assignments = buildString {
            input.players.forEach {
                appendLine("P${it.number}: ${it.channelText()} connected=${it.connected} descriptor=${it.descriptor ?: "n/a (HDMI/injected or not assigned)"}")
            }
            if (input.previousAssignments.isNotEmpty()) appendLine("previous run: ${input.previousAssignments.joinToString(" · ")}")
            appendLine("same-channel assignment rejects=${input.sameChannelRejects} · unassigned-channel inputs=${input.unassignedInputs}")
        }
        val settings = "difficulty=${difficulty.label} (this run: ${runDifficulty.label}) · movement=${moveMode.label} · smoothing=${if (input.smoothingMs == 0L) "off" else "${input.smoothingMs} ms"} · " +
            "diagnostics panel=${if (showDiagnostics) "on" else "off"} · music $musicVolume% · sound effects $sfxVolume% · high score $hiScore\n" +
            "player speed 0.75 px/frame · ice slide $ICE_SLIDE_PX px · fire: one shot per fresh press of OK/Enter/Numpad Enter/Space, held $HOLD_FIRE_MS ms or longer it keeps firing, " +
            "no cooldown timer but a bullet slot recovers $SLOT_RECOVERY_FRAMES frames after an impact (ROM), 1 bullet in flight (2 with 2+ stars), key repeats ignored\n"
        val rules = buildString {
            appendLine("enemies destroyed by type (basic/fast/power/armor): ${killsByType.joinToString("/")} · max on field $maxConcurrent · " +
                "frames a spawn waited for a free slot or spawn point $spawnWaitFrames · enemy shots $enemyShots")
            appendLine("bullet clashes $clashes · hits absorbed by shields $shieldBlocks · bricks broken ${terrain.bricksBroken} · steel tiles broken ${terrain.steelBroken} (this stage)")
            appendLine("items dropped ${PowerUpType.entries.joinToString(" ") { "${it.label}=${dropsByType[it.ordinal]}" }}")
            appendLine("items collected ${PowerUpType.entries.joinToString(" ") { "${it.label}=${pickupsByType[it.ordinal]}" }}")
            appendLine("AI goals ${GoalKind.entries.joinToString(" ") { "$it=${ai.goalCounts[it.ordinal]}" }} · HQ lanes ${Lane.entries.joinToString(" ") { "${it.label}=${ai.laneCounts[it.ordinal]}" }} · " +
                "lane arrivals ${ai.laneArrivals} · no-progress events ${ai.stuckEvents} · nav rebuilds ${nav.rebuilds} · distance maps ${nav.fieldsBuilt}")
        }
        val firing = buildString {
            input.players.forEachIndexed { i, inp ->
                val p = players[i]
                appendLine("P${p.index + 1}: fire presses=${inp.okPresses} · shots=${p.shots} · refused (cooldown or bullet limit)=${p.shotsRefused} · " +
                    "fire repeats ignored=${inp.okRepeatsIgnored} · smoothing grace ended by fire=${inp.graceEndedByFire}")
                if (inp.isHdmi) {
                    appendLine("    fire pressed ≤${CoopInput.OK_AFTER_TIMEOUT_WINDOW_MS} ms after a suspected CEC timeout UP=" +
                        "${inp.okPressesAfterSuspectedTimeout} (heuristic: a held OK re-sent by the TV, or a fast re-tap)")
                }
            }
            appendLine("A press fires once; holding a fire key $HOLD_FIRE_MS ms or longer keeps firing, so shots can outnumber presses.")
        }
        val simultaneous = "Observation: both tanks alive and moving in the same 60 Hz steps (2-player runs).\n" +
            "segments ≥ 0.5 s: $bothMovingSegments · longest %.1f s · total %.1f s\n".format(bothMovingLongest / 60.0, bothMovingTotal / 60.0)
        val heuristics = buildString {
            appendLine("Heuristics only – the log cannot show what the physical buttons did.")
            input.players.forEach {
                appendLine("P${it.number}: downs=${it.downs} ups=${it.ups} repeats=${it.repeats} · " +
                    "re-press ≤$QUICK_REPRESS_MS ms=${it.quickRepresses} · re-press $QUICK_REPRESS_MS–$SHORT_REPRESS_MS ms=${it.shortRepresses} · " +
                    "suspected CEC timeout UPs=${it.suspectedTimeoutReleases}")
                appendLine("    smoothing: gaps bridged=${it.bridgedGaps} · grace expiries (movement continued after a release)=${it.graceExpiries} · " +
                    "direction kept active by grace=${it.graceMovementMs} ms")
            }
        }
        val frameText = buildString {
            appendLine(frames.summary() + " · 60 Hz steps in the last frame $stepsLastFrame · long gaps capped (simulation time dropped) $catchUpDrops")
            appendLine("Frame intervals come from the UI frame clock. A long frame means rendering or the main thread stalled; " +
                "compare its time with the key events and the audio line below before blaming the remote.")
            frames.stalls.forEach { appendLine("  $it") }
        }
        val terrainText = "Current terrain (52×52 cells of 4 px; . empty B brick S steel W water F forest I ice):\n" +
            terrain.snapshotRows().joinToString("\n") + "\n"
        return listOf(
            "Tank co-op – state" to state,
            "Session caveats" to CAVEATS.joinToString("\n") { "- $it" },
            "Remote assignments" to assignments,
            "Settings" to settings,
            "Rules and stage statistics" to rules,
            "Firing" to firing,
            "Simultaneous movement" to simultaneous,
            "Input observations per player" to heuristics,
            "Frame timing" to frameText,
            "Audio" to (audio.statsText() + "\n"),
            "Match log (oldest first)" to events.joinToString("\n"),
            "Terrain snapshot" to terrainText,
        )
    }

    companion object {
        const val STEP_NS = 1_000_000_000L / FPS
        const val MAX_STEPS = 6
        const val MAX_FRAME_NS = 250_000_000L

        const val ENEMIES_PER_STAGE = 20
        /** ROM: blinking item carriers are flagged when 17, 10 and 3 enemies remain, i.e. the 4th, 11th and 18th. */
        const val START_LIVES = 3
        const val MAX_STARS = 3
        const val DEFAULT_HI_SCORE = 20000
        const val POWERUP_POINTS = 500

        const val PLAYER_SPEED_Q = 3
        /** ROM: after the start lock, a released tank slides up to 15 px on ice (the 13-px start lock is not used). */
        const val ICE_SLIDE_PX = 15
        const val SLOT_RECOVERY_FRAMES = 9
        const val REVEAL_FRAMES = 45
        const val ENEMY_FIRE_ONE_IN = 32

        const val PLAYER_SPAWN_STAR_FRAMES = 37
        const val ENEMY_SPAWN_STAR_FRAMES = 56
        const val SPAWN_HELMET_TICKS = 3
        const val RESPAWN_DELAY_FRAMES = 32
        /** Co-op revival countdown: 10 s of game time. */
        const val REVIVE_FRAMES = 600
        const val HELMET_TICKS = 10
        const val CLOCK_TICKS = 10
        const val SHOVEL_TICKS = 20
        /** ROM: last enemy's explosion (48 frames) plus 128 frames before the tally. */
        const val CLEAR_WAIT_FRAMES = 176
        /** ROM: GAME OVER rises 1 px per frame for 127 frames and the field runs on until frame 256. */
        const val GAME_OVER_FRAMES = 256
        const val GAME_OVER_RISE = 127
        const val INTRO_FRAMES = 100
        /** Longest the results screen waits, past its own end, for the victory jingle to finish (15 s). */
        const val JINGLE_HOLD_MAX_FRAMES = 900
        /** Online: the results screen holds for the bundled Got High Score file (9.1 s) from its start. */
        const val ONLINE_JINGLE_FRAMES = 552
        /** Half a second: FIRE still held or tapped as the field closes does not skip the results screen. */
        const val TALLY_SKIP_GUARD_FRAMES = 30
        /** Fire held this long keeps firing (whenever a bullet slot is free); a shorter press fires once. */
        const val HOLD_FIRE_MS = 250L
        /** A tap on the touch FIRE button or a click both end PRESS START and press a key: only the first counts. */
        const val PRESS_START_GUARD_MS = 300L
        const val TITLE_SCROLL = 224
        /** The engine loop waits for the Stage Start cue (about 4.4 s) so the two never sound together. */
        const val STAGE_START_CUE_FRAMES = 270
        const val STAGE_START_MAX_FRAMES = 900
        const val MAX_EVENTS = 3000

        private const val HIT_BRICK = 1
        private const val HIT_STEEL = 2
        private const val HIT_STEEL_BROKEN = 3

        /** ROM item points (top-left, field pixels): x and y each from this list. */
        val ITEM_POINTS = listOf(24, 72, 120, 168)

        /** EASY with two players: the minimum gap between arrivals at these stages (4.2, 3.8, 3.2, 2.7, 2.5 s). */
        val EASY_TWO_PLAYER_GAPS = listOf(1 to 252, 10 to 228, 20 to 192, 30 to 162, 35 to 150)

        /** [EASY_TWO_PLAYER_GAPS] at [stage], on a straight line between the listed stages (frames, rounded). */
        fun easyTwoPlayerGap(stage: Int): Int {
            val points = EASY_TWO_PLAYER_GAPS
            val s = stage.coerceIn(points.first().first, points.last().first)
            val i = points.indexOfFirst { it.first >= s }
            if (i == 0) return points[0].second
            val (s0, f0) = points[i - 1]
            val (s1, f1) = points[i]
            return (f0 + (f1 - f0) * (s - s0).toDouble() / (s1 - s0)).roundToInt()
        }

        val TITLE_ITEMS = listOf(MenuItem.ONE_PLAYER, MenuItem.TWO_PLAYERS, MenuItem.ONLINE, MenuItem.SETTINGS, MenuItem.EXIT)
        val PHONE_TITLE_ITEMS = TITLE_ITEMS - MenuItem.TWO_PLAYERS
        /** Online, the pause menu keeps what both phones share; volumes, smoothing and diagnostics are per phone. */
        val ONLINE_PAUSE_ITEMS = listOf(MenuItem.RESUME, MenuItem.RESTART, MenuItem.MOVEMENT, MenuItem.QUIT)
        /** Difficulty is only here, not in the pause menu: a run keeps the difficulty it started with. */
        val SETTINGS_ITEMS = listOf(MenuItem.DIFFICULTY, MenuItem.MOVEMENT, MenuItem.SMOOTHING, MenuItem.MUSIC, MenuItem.SFX, MenuItem.DIAGNOSTICS, MenuItem.SAVE_REPORT, MenuItem.BACK)
        val PAUSE_ITEMS = listOf(
            MenuItem.RESUME, MenuItem.RESTART, MenuItem.MOVEMENT, MenuItem.SMOOTHING, MenuItem.MUSIC, MenuItem.SFX,
            MenuItem.DIAGNOSTICS, MenuItem.SAVE_REPORT, MenuItem.QUIT,
        )

        val CAVEATS = listOf(
            "Quick re-press counts (same direction pressed again within $QUICK_REPRESS_MS ms / $SHORT_REPRESS_MS ms of its release) are " +
                "heuristics, not proven hardware faults: a fast deliberate tap looks the same in the event log.",
            "An UP ~550 ms after the last HDMI-CEC message is a SUSPECTED CEC safety-timeout release, not proof that a physical " +
                "release was lost: physical press durations are not recorded.",
            "Repeat intervals and dispatch lag are not button-to-screen latency, which this app cannot measure.",
            "Between earlier hardware sessions the HDMI cable/input was changed, devices were restarted and HDMI-CEC was toggled; " +
                "which of those actions restored CEC input was not isolated.",
        )
    }
}

class FrameStats {
    var frames = 0
        private set
    var maxMs = 0.0
        private set
    var over34 = 0
        private set
    var over50 = 0
        private set
    var over100 = 0
        private set
    var recentMs = 0.0
        private set
    private var sumMs = 0.0
    val stalls = ArrayDeque<String>()

    val avgMs get() = if (frames == 0) 0.0 else sumMs / frames

    fun record(ms: Double, wallTime: Long, stageFrame: Int) {
        frames++
        sumMs += ms
        maxMs = max(maxMs, ms)
        recentMs = if (frames == 1) ms else recentMs * 0.9 + ms * 0.1
        if (ms > 34) over34++
        if (ms > 50) {
            over50++
            stalls.addLast("${clockTime(wallTime)}  frame %.0f ms  (stage t=%.1f s)".format(ms, stageFrame / 60.0))
            if (stalls.size > 60) stalls.removeFirst()
        }
        if (ms > 100) over100++
    }

    fun summary() = "frames $frames · avg %.1f ms · max %.0f ms · >34 ms: %d · >50 ms: %d · >100 ms: %d"
        .format(avgMs, maxMs, over34, over50, over100)
}
